import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';
import NDSelect from './NDSelect';
import '../coldemailer.css';
import '../coldemailer-styles.css';

const c_StatusTest = 'Test';
const c_StatusLive = 'Live';
const c_StatusLivePersonal = 'LivePersonal';

const c_ModeOptions = [
	{ value: c_StatusTest, label: 'Test' },
	{ value: c_StatusLivePersonal, label: 'Live Personal' },
	{ value: c_StatusLive, label: 'Live' }
];

// Compression presets, expressed as how long one day should last. The same list serves both
// compressing modes; Test and Live Personal keep their own stored factor.
const c_CompressionOptions = [
	{ value: 1440, label: '1 day = 1 minute' },
	{ value: 288, label: '1 day = 5 minutes' },
	{ value: 180, label: '1 day = 8 minutes' },
	{ value: 144, label: '1 day = 10 minutes' },
	{ value: 48, label: '1 day = 30 minutes' },
	{ value: 24, label: '1 day = 1 hour' }
];

const c_DefaultTestCompression = 1440;
const c_DefaultLivePersonalCompression = 180;

// Always visible, so the current mode is never in doubt. Loud in the compressing modes, quiet
// in Live, and explicit about whether anything actually leaves the server.
function TestModeBanner({ on_status_change }) {
	const [test_status, set_test_status] = useState(null);
	const [compression, set_compression] = useState(c_DefaultTestCompression);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');

	// The compression shown follows the mode, since each mode stores its own factor
	const apply_status_to_state = (status) => {
		set_test_status(status);

		if(status.system_status === c_StatusLivePersonal) {
			set_compression(status.live_personal_compression_factor > 1 ? status.live_personal_compression_factor : c_DefaultLivePersonalCompression);
			return;
		}

		set_compression(status.time_compression_factor > 1 ? status.time_compression_factor : c_DefaultTestCompression);
	};

	const load_test_status = async () => {
		try {
			const status = await coldemailer_api.get_test_status();
			apply_status_to_state(status);
		} catch (error) {
			console.log('TestModeBanner.load_test_status - ERROR: ', error);
			set_error('Could not read the service mode.');
		}
	};

	useEffect(function() {
		load_test_status();
	}, []);

	const apply_status = async (system_status, time_compression_factor) => {
		set_saving(true);
		set_error('');

		try {
			const status = await coldemailer_api.set_test_status(system_status, time_compression_factor);
			apply_status_to_state(status);

			if(on_status_change) {
				on_status_change(status);
			}
		} catch (error) {
			console.log('TestModeBanner.apply_status - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Could not change the service mode.';
			set_error(message);
		} finally {
			set_saving(false);
		}
	};

	const on_mode_change = (event) => {
		const system_status = event.target.value;

		if(system_status === c_StatusLive) {
			apply_status(system_status, null);
			return;
		}

		const factor = system_status === c_StatusLivePersonal ? c_DefaultLivePersonalCompression : compression;
		apply_status(system_status, factor);
	};

	const on_compression_change = (event) => {
		const factor = parseInt(event.target.value, 10);
		set_compression(factor);

		apply_status(test_status ? test_status.system_status : c_StatusLive, factor);
	};

	const on_sending_change = async (event) => {
		set_saving(true);
		set_error('');

		try {
			const status = await coldemailer_api.set_enable_sending(event.target.checked);
			apply_status_to_state(status);
		} catch (error) {
			console.log('TestModeBanner.on_sending_change - ERROR: ', error);
			set_error('Could not change the sending setting.');
		} finally {
			set_saving(false);
		}
	};

	const get_bar_class = () => {
		if(!test_status) {
			return 'ce-mode-bar ce-mode-bar-live';
		}

		if(test_status.system_status === c_StatusTest) {
			return 'ce-mode-bar ce-mode-bar-test';
		}

		if(test_status.system_status === c_StatusLivePersonal) {
			return 'ce-mode-bar ce-mode-bar-personal';
		}

		return 'ce-mode-bar ce-mode-bar-live';
	};

	const get_tag_class = () => {
		if(!test_status) {
			return 'ce-mode-tag ce-mode-tag-live';
		}

		if(test_status.system_status === c_StatusTest) {
			return 'ce-mode-tag ce-mode-tag-test';
		}

		if(test_status.system_status === c_StatusLivePersonal) {
			return 'ce-mode-tag ce-mode-tag-personal';
		}

		return 'ce-mode-tag ce-mode-tag-live';
	};

	const get_tag_label = () => {
		if(!test_status) {
			return 'LIVE';
		}

		if(test_status.system_status === c_StatusTest) {
			return 'TEST MODE';
		}

		if(test_status.system_status === c_StatusLivePersonal) {
			return 'LIVE PERSONAL';
		}

		return 'LIVE';
	};

	const render_loading = () => {
		return (
			<div className="ce-mode-bar ce-mode-bar-live">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_detail = () => {
		if(test_status.system_status === c_StatusLive) {
			return (
				<span className="ce-mode-text">
					Running in real time against every recipient.
					{test_status.sending_enabled ? ' Sending is ON.' : ' Sending is off, nothing leaves the server.'}
				</span>
			);
		}

		if(test_status.system_status === c_StatusLivePersonal) {
			return (
				<span className="ce-mode-text">
					Time compressed {test_status.active_compression_factor}x, one day lasts {test_status.compressed_day_seconds} seconds.
					Only the {test_status.allowlisted_addresses} allowlisted address{test_status.allowlisted_addresses === 1 ? '' : 'es'} can be contacted.
					{test_status.sending_enabled ? ' Sending is ON, these are real emails.' : ' Sending is off, nothing leaves the server.'}
				</span>
			);
		}

		return (
			<span className="ce-mode-text">
				Time compressed {test_status.active_compression_factor}x, one day lasts {test_status.compressed_day_seconds} seconds.
				Schedules and recycle dates are not real.
				{test_status.paused_campaigns ? ' Paused ' + test_status.paused_campaigns + ' active campaign' + (test_status.paused_campaigns === 1 ? '' : 's') + '.' : ''}
				{test_status.sending_enabled ? ' Sending is ON.' : ' Sending is off, nothing leaves the server.'}
			</span>
		);
	};

	if(!test_status) {
		return render_loading();
	}

	const shows_compression = test_status.system_status !== c_StatusLive;

	return (
		<div className={get_bar_class()}>
			<span className={get_tag_class()}>
				{get_tag_label()}
			</span>

			{error ? <span className="ce-mode-text">{error}</span> : render_detail()}

			<div className="ce-mode-spacer"></div>

			{shows_compression ? (
				<NDSelect
					size="sm"
					className="ce-mode-compression"
					options={c_CompressionOptions}
					value={compression}
					onChange={on_compression_change}
					disabled={saving}
				/>
			) : null}

			<NDSelect
				size="sm"
				className="ce-mode-select"
				options={c_ModeOptions}
				value={test_status.system_status}
				onChange={on_mode_change}
				disabled={saving}
			/>

			<Form.Check
				type="switch"
				id="ce-sending-switch"
				className="ce-mode-switch"
				label="Sending"
				checked={test_status.sending_enabled}
				onChange={on_sending_change}
				disabled={saving || !test_status.sending_allowed_in_code}
			/>
		</div>
	);
}

export default TestModeBanner;
